import { IIconBandSetting } from "Bands/types";
import { eventStatuses, peopleBlockStatuses } from "Bands/utils";
import { copy } from "shared/assets";
import {
  Clock,
  Guests,
  Hotel,
  Plane,
  UserMultiple,
  Venue,
} from "shared/assets/icons";
import { LocalStorageService } from "shared/services";
import {
  IFieldType,
  IFormFields,
  IFormOption,
  ILocalStorageKey,
} from "shared/types";
import { getIcon } from "shared/utils";

const { errors } = copy;
const xlsFields = copy.calendar.xlsExport.fields;

export const getFields = ({
  bandId,
  eventTypes,
  infoBlockTypes,
}: {
  bandId: string;
  eventTypes: IIconBandSetting[];
  infoBlockTypes: IIconBandSetting[];
}): IFormFields => {
  const savedExportPreferences = LocalStorageService.getItem(
    ILocalStorageKey.xlsExportPreferencesPerBand
  )?.[bandId];

  const getPreferenceForBlock = (blockType: string) => {
    const savedBlockPreferences = savedExportPreferences?.eventBlocks?.find(
      (block: any) =>
        typeof block === "object" && Object.keys(block)?.[0] === blockType
    );

    if (!savedBlockPreferences) return null;

    return Object.values(savedBlockPreferences)?.[0];
  };

  const eventFieldOptions: IFormOption[] = [
    {
      label: "Venue",
      value: "/api/event_venue_blocks",
      icon: Venue,
    },
    {
      label: "People",
      value: "/api/event_people_blocks",
      icon: UserMultiple,
    },
    {
      label: "Schedule",
      value: "/api/event_schedule_blocks",
      icon: Clock,
      options: [
        {
          label: "Full schedule",
          value: "fullSchedule",
          defaultChecked:
            "fullSchedule" ===
              getPreferenceForBlock("/api/event_schedule_blocks") ||
            "onlyPersonalSchedule" !==
              getPreferenceForBlock("/api/event_schedule_blocks"),
        },
        {
          label: "My schedule",
          value: "onlyPersonalSchedule",
          defaultChecked:
            "onlyPersonalSchedule" ===
            getPreferenceForBlock("/api/event_schedule_blocks"),
        },
      ],
    },
    {
      label: "Guestlist",
      value: "/api/event_guest_list_blocks",
      icon: Guests,
      options: [
        {
          label: "All guests",
          value: "allGuests",
          defaultChecked:
            "allGuests" ===
              getPreferenceForBlock("/api/event_guest_list_blocks") ||
            "onlyPersonalGuests" !==
              getPreferenceForBlock("/api/event_guest_list_blocks"),
        },
        {
          label: "My guests",
          value: "onlyPersonalGuests",
          defaultChecked:
            "onlyPersonalGuests" ===
            getPreferenceForBlock("/api/event_guest_list_blocks"),
        },
      ],
    },
    {
      label: "Travel",
      value: "/api/event_travel_blocks",
      icon: Plane,
      options: [
        {
          label: "All travel info",
          value: "allTravelInfo",
          defaultChecked:
            "allTravelInfo" ===
              getPreferenceForBlock("/api/event_travel_blocks") ||
            "onlyPersonalTravelInfo" !==
              getPreferenceForBlock("/api/event_travel_blocks"),
        },
        {
          label: "My travel info",
          value: "onlyPersonalTravelInfo",
          defaultChecked:
            "onlyPersonalTravelInfo" ===
            getPreferenceForBlock("/api/event_travel_blocks"),
        },
      ],
    },
    {
      label: "Accommodation",
      value: "/api/event_accommodation_blocks",
      icon: Hotel,
      options: [
        {
          label: "All accommodations",
          value: "allAccommodations",
          defaultChecked:
            "allAccommodations" ===
              getPreferenceForBlock("/api/event_accommodation_blocks") ||
            "onlyPersonalAccommodationInfo" !==
              getPreferenceForBlock("/api/event_accommodation_blocks"),
        },
        {
          label: "My accommodations",
          value: "onlyPersonalAccommodationInfo",
          defaultChecked:
            "onlyPersonalAccommodationInfo" ===
            getPreferenceForBlock("/api/event_accommodation_blocks"),
        },
      ],
    },
    ...infoBlockTypes.map(({ icon, id, ...props }) => ({
      label: props.label,
      value: id,
      icon: getIcon(icon),
    })),
  ];
  return [
    {
      name: "startDate",
      label: xlsFields.startDate,
      type: IFieldType.datePicker,
      validation: {
        required: errors.required,
      },
      style: {
        gridColumn: "span 1",
      },
    },
    {
      name: "endDate",
      label: xlsFields.endDate,
      type: IFieldType.datePicker,
      validation: {
        required: errors.required,
      },
      style: {
        gridColumn: "span 1",
      },
    },
    {
      name: "eventRSVPStatuses",
      label: xlsFields.eventRSVPStatuses,
      type: IFieldType.tagGroup,
      options: peopleBlockStatuses,
      isMultiple: true,
      showViewLabel: true,
      style: {
        gridColumn: "span 2",
      },
    },
    {
      name: "eventStatuses",
      label: xlsFields.eventStatuses,
      type: IFieldType.tagGroup,
      options: eventStatuses,
      isMultiple: true,
      validation: {
        required: errors.required,
      },
      style: {
        gridColumn: "span 2",
      },
    },
    {
      name: "eventTypes",
      label: xlsFields.eventTypes,
      type: IFieldType.tagGroup,
      options: eventTypes?.map(({ icon, id, ...props }) => ({
        ...props,
        value: id,
        icon: getIcon(icon),
      })),
      isMultiple: true,
      validation: {
        required: errors.required,
      },
      style: {
        gridColumn: "span 2",
      },
    },
    {
      name: "eventBlocks",
      label: xlsFields.eventBlocks,
      type: IFieldType.tagGroup,
      options: eventFieldOptions,
      isMultiple: true,
      style: {
        gridColumn: "span 2",
      },
    },
  ];
};
